// The screens this app has, generated from .appfactory/product-plan.json.
//
// Imported by the navigation shell rather than replacing it: the shell owns
// router.js — bottom-tabs, stack and sidebar produce different routers, and it
// rewrites that file on every `spec apply`. Two owners for one file is a silent
// fight, so the product contributes ROUTES and the shell decides what to do
// with them.
//
// `primary` marks a screen that earns a place in the navigation itself.
//
// `tab` and `backName` are this app's own, and `src/nav-origin.js` is what
// reads them. A pushed screen still belongs to a tab — the journey is in Trip,
// the train card is in Trains — and the tab bar had no way of knowing it,
// because `ion-tab-bar` selects a button by matching the path against that
// button's own href and `/tabs/train/…` matches none of the four. `tab` is
// where a screen sits when nobody pushed it; a TAB ROUTE names itself, which is
// how one is told from a pushed screen. `backName` is what another screen's
// back chip calls this one, so no page has to hard-code the name of whatever
// happens to be underneath it today.

import HomePage from './pages/HomePage.vue';
import { servedAdapterModes } from './modes.js';

// Home is in the main chunk because it is the first paint. Every other screen
// is fetched the first time it is opened — Vite splits each into its own
// file, so the departures board, the line strip and its network data are not
// part of what a cold open has to download.

const BASE_ROUTES = [
  {
    id: 'home',
    path: '/',
    label: 'Your Lines',
    component: HomePage,
    primary: true,
    tab: 'home',
    backName: 'YOUR LINES',
  },
  {
    id: 'lines',
    path: '/lines',
    label: 'Choose Lines',
    component: () => import('./pages/LinesPage.vue'),
    primary: false,
    tab: 'home',
    backName: 'CHOOSE LINES',
  },
  {
    id: 'alerts',
    path: '/alerts',
    label: 'Recent Alerts',
    component: () => import('./pages/AlertsPage.vue'),
    primary: false,
    tab: 'alerts',
    backName: 'ALERTS',
  },
  {
    id: 'departures',
    path: '/departures',
    label: 'Departures',
    component: () => import('./pages/DeparturesPage.vue'),
    primary: false,
    tab: 'departures',
    backName: 'TRAINS',
  },
  {
    // Pushed from Settings, never a tab. Rules are set once and then left
    // alone — the screen that earns a place in the bar is the one you open
    // every morning, and this is not it.
    id: 'rules',
    path: '/rules',
    label: 'Commute Rules',
    component: () => import('./pages/RulesPage.vue'),
    primary: false,
    tab: 'home',
    backName: 'RULES',
  },
  {
    // `new` for a rule that does not exist yet, an id to edit one. Memory
    // history carries no route state, so the id in the path is the whole of
    // what this screen is handed.
    id: 'rule',
    path: '/rule/:id',
    label: 'Rule',
    component: () => import('./pages/RuleEditPage.vue'),
    primary: false,
    tab: 'home',
    backName: 'RULE',
  },
  {
    id: 'settings',
    path: '/settings',
    label: 'Settings',
    component: () => import('./pages/SettingsPage.vue'),
    primary: false,
    tab: 'home',
    backName: 'SETTINGS',
  },
  {
    // Hidden: opened from the version row in Settings after seven taps.
    id: 'diagnostics',
    path: '/diagnostics',
    label: 'Diagnostics',
    component: () => import('./pages/DiagnosticsPage.vue'),
    primary: false,
    tab: 'home',
    backName: 'DIAGNOSTICS',
  },
  {
    id: 'line',
    path: '/line/:routeId',
    label: 'Line',
    component: () => import('./pages/LinePage.vue'),
    primary: false,
    tab: 'home',
    backName: 'LINE',
  },
  {
    id: 'train',
    path: '/train/:runRef/:stopId',
    label: 'Train',
    component: () => import('./pages/TrainPage.vue'),
    primary: false,
    tab: 'departures',
    backName: 'TRAIN',
  },
  {
    // Pushed from the board's masthead, never a tab. `TAB_IDS` in router.js is
    // untouched — a station is somewhere you go FROM
    // the board rather than a destination of its own.
    id: 'station',
    path: '/station/:stopId',
    label: 'Station',
    component: () => import('./pages/StationPage.vue'),
    primary: false,
    tab: 'departures',
    backName: 'STATION',
  },
  {
    // The drawn network, and a TAB. It used to be pushed from the Lines tab's
    // footer and a Settings row, three taps from where anybody starts; when
    // the Trip tab was retired (the board with a destination plans the same
    // journey) the map took its slot in the bar. Lines and Settings still
    // offer it, and both now switch to this tab rather than pushing a second
    // copy of it onto their own stacks.
    id: 'map',
    path: '/map',
    label: 'Network map',
    component: () => import('./pages/MapPage.vue'),
    primary: false,
    // Pushed again from Lines and Settings: Signal Box took its tab slot.
    tab: 'home',
    backName: 'MAP',
  },
  {
    // The journey is an INDEX, not an id. This app's router uses memory
    // history, which carries no route state, so the planned options live in
    // `src/directions-state.js` and this is which of them to draw. Opened with
    // an empty stash the page says so rather than inventing a train. Opened
    // from the board's planned rows, so its fallback tab is Trains; Options
    // records its own through `openScreen`.
    id: 'journey',
    path: '/journey/:index',
    label: 'Journey',
    component: () => import('./pages/JourneyPage.vue'),
    primary: false,
    tab: 'departures',
    backName: 'JOURNEY',
  },
  {
    // Pushed from the card on Alerts, the card above the board, the button
    // on a disruption or a line, and from a notification's own quick action.
    // Never a tab: it is the screen for the evening a line goes down, and a
    // bar that offered it every morning would be spending a slot on a bad day.
    id: 'options',
    path: '/options',
    label: 'Your options',
    component: () => import('./pages/OptionsPage.vue'),
    primary: false,
    // Opened from Alerts, from the board, from a notice and from a line — four
    // screens in three different tabs — so the tab here is only the fallback
    // for a page nobody pushed. `openScreen` records the real one.
    tab: 'home',
    backName: 'YOUR OPTIONS',
  },
  {
    // The game, pushed from a Settings row and from the board when a train on
    // it is late. A TAB since 25 September 2026 (it took the map's slot): the
    // owner wanted the game one tap away while a train is late. `tab` is the fallback for a page nobody pushed; the board's
    // link records the Trains tab through `openScreen`.
    id: 'signal-box',
    path: '/signal-box',
    label: 'Signal Box',
    component: () => import('./pages/SignalBoxPage.vue'),
    primary: false,
    tab: 'signal-box',
    backName: 'SIGNAL BOX',
  },
  {
    id: 'disruption-detail',
    path: '/disruption/:id',
    label: 'Disruption',
    component: () => import('./pages/DisruptionDetailPage.vue'),
    primary: false,
    tab: 'home',
    backName: 'DISRUPTION',
  },
];

// ── THE SCREENS OF EVERY MODE WITH STOPS OF ITS OWN ─────────────────────────
//
// Generated from the registry (src/modes.js): three pushed screens and no tab.
// A route is opened from its row on Your lines, a stop from the route's strip,
// a vehicle from a stop's board — the same three steps a train takes from line
// to board to card. All of them are in the Lines tab, because that is where a
// route is. There is NO PLAN SCREEN and NO TRIP SCREEN: a trip is planned on the
// board of a stop, with a destination set, and read on the journey page, as a
// train's is. Their old paths (`tram-plan`, `tram-trip`) are redirects, see
// src/trip-redirect.js. Melbourne's trams come out as `tram`, `tram-stop` and
// `tram-run`, exactly the ids and paths they have always had; a new mode gets
// the same three under its own id, and the pages read their mode from
// `meta.mode`.
const modeRoutes = (mode) => {
  const id = mode.id;
  const up = (word) => word.charAt(0).toUpperCase() + word.slice(1);
  const screen = (suffix, path, label, load, backName) => ({
    id: suffix ? `${id}-${suffix}` : id,
    path,
    label,
    component: load,
    primary: false,
    tab: 'home',
    backName,
    meta: { mode: id },
  });
  return [
    screen('', `/${id}/:routeId`, `${up(mode.labels.one)} ${mode.labels.line}`, () => import('./pages/ModeRoutePage.vue'), 'ROUTE'),
    screen('stop', `/${id}-stop/:stopId`, `${up(mode.labels.one)} ${mode.labels.stop}`, () => import('./pages/ModeStopPage.vue'), 'STOP'),
    screen('run', `/${id}-run/:runRef/:stopId`, up(mode.labels.one), () => import('./pages/ModeRunPage.vue'), mode.labels.one.toUpperCase()),
  ];
};

export const PRODUCT_ROUTES = [
  ...BASE_ROUTES,
  ...servedAdapterModes().filter((m) => m.api).flatMap(modeRoutes),
];
